// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';
import _ from 'lodash';

import { UserDO } from 'wdna-components/lib/types.new';
import { GridApi, RowDataUpdatedEvent, GridReadyEvent, IRowNode } from 'wdna-grid-table/lib/types';
import Table, { TableButtonsConfig } from 'wdna-components/lib/table/Table';
import { TabConfig, addTab, removeTab } from 'wdna-components/lib/tabs';
import { messageError, messageInfo, messageWarning, messageBoxText, messageBoxInfo } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';
import { hasLevel } from 'wdna-components/lib/helpers/permission-new';

import { alarm, AlarmDO, MsgRS } from '../../requests';

import Edit from './Edit';

type Props = unknown;

interface State {
  loading: boolean;
  showGrid?: boolean;
  update?: string;
  marked: number;

  data?: AlarmDO[];
}

export default class List extends React.Component<Props, State> {
  private pid: string;
  private mounted: boolean;
  private buttons: TableButtonsConfig;
  private gridApi: GridApi | undefined;

  private userIsCLI: boolean;

  public constructor(props: Props) {
    super(props);

    this.pid = window.getStr();
    this.mounted = false;
    this.userIsCLI = false;

    this.buttons = {
      add: hasLevel('role', ['ADM', 'URW']),
      del: hasLevel('role', ['ADM', 'URW']),
      edit: true,
      copy: hasLevel('role', ['ADM', 'URW']),
    };

    this.state = {
      loading: false,
      showGrid: false,
      update: window.getStr(),
      marked: 0,

      data: [],
    };
  }

  public componentDidMount(): void {
    this.mounted = true;
    this.userIsCLI = !hasLevel('role', ['ADM', 'URW', 'URO']);

    this.load().catch(() => { /* pass */ });
  }

  public shouldComponentUpdate(nextProps: Props, nextState: State): boolean {
    return this.state.update !== nextState.update;
  }

  public componentWillUnmount(): void {
    this.mounted = false;
  }

  public setLocalState = (nextState: Partial<State>, callback?: (() => void)): void => {
    if (this.mounted) {
      this.setState((prevState: State) => ({
        ...prevState,
        ...nextState,
      }), callback);
    }
  };

  public load = async (): Promise<void> => {
    const user: UserDO = _.get(window.store.getState(), ['auth', 'user']);

    this.loadingStart();
    try {
      const alarmsArray: AlarmDO[] = await alarm<AlarmDO[]>('list') || [];
      const data: AlarmDO[] = alarmsArray.filter((item: AlarmDO) => {
        return (!this.userIsCLI || (user.access?.companyId && item.companyId === parseInt(user.access.companyId, 10)));
      });

      this.setLocalState({
        data,
        marked: 0,
      });
    } catch (error) {
      messageError('Error', getErrorMessage(error));
    }
    this.loadingFinish();
  };

  public loadingStart = (): void => {
    this.setLocalState({
      update: window.getStr(),
      loading: true,
    });
  };

  public loadingFinish = (): void => {
    this.setLocalState({
      update: window.getStr(),
      loading: false,
    });
  };

  public onDelete = (item: AlarmDO): void => {
    const confirm: string = String(item.id);
    messageBoxText('Delete', `Enter '${confirm}' to confirm you want to delete the item: ${item.id}?`, (value?: string) => {
      if (confirm === value) {
        this.loadingStart();
        alarm('del', String(item.id))
          ?.then(() => {
            messageInfo('Success', 'Register successfully deleted', 5000);
            this.load().catch(() => { /* pass */ });
          })
          .catch((error: any) => {
            messageError('Error', getErrorMessage(error));
          })
          .finally(() => {
            this.loadingFinish();
          });
      } else {
        messageWarning('Warning', 'Wrong input parameter', 5000);
      }
    }, 'remove-circle', undefined, confirm);
  };

  public onEdit = (item?: AlarmDO): void => {
    window.store.dispatch(removeTab('AE'));
    const rowId: number | undefined = item?.id;
    const duplicate: boolean = false;
    setTimeout(() => {
      this.loadTab(duplicate, rowId);
    }, 0);
  };

  public onCopy = (item: AlarmDO): void => {
    window.store.dispatch(removeTab('AE'));
    const rowId: number = item.id;
    const duplicate: boolean = true;
    setTimeout(() => {
      this.loadTab(duplicate, rowId);
    }, 0);
  };

  public loadTab = (duplicate: boolean, rowId?: number): void => {
    const tabId: string = 'AE';
    const title: string = rowId ? `Alarm Edit: ${rowId}` : 'Alarm New';

    window.store.dispatch(removeTab(tabId));
    const tabData: TabConfig = {
      id: tabId,
      active: true,
      title,
      component: Edit,
      props: {
        rowId,
        tabId: this.pid,
        report: 'ALARMS',
        title: 'Alarms',
        notify: this.notify,
        duplicate,
      },
    };
    window.store.dispatch(addTab(tabData));
  };

  public notify = (): void => {
    this.load().catch(() => { /* pass */ });
  };

  public rowDataUpdated = (_event: RowDataUpdatedEvent): void => {
    this.setLocalState({
      update: window.getStr(),
      showGrid: true,
    });
  };

  public countSelected = (): void => {
    let marked: number = 0;
    if (this.gridApi) {
      marked = this.gridApi.getSelectedNodes().length;
    }
    this.setLocalState({
      update: window.getStr(),
      marked,
    });
  };

  public activateSelected = (): void => {
    messageBoxInfo('Activate', 'Are you sure you want to activate the selected alarms?', () => {
      this.bulkActivation(true);
    });
  };

  public deactivateSelected = (): void => {
    messageBoxInfo('Deactivate', 'Are you sure you want to deactivate the selected alarms?', () => {
      this.bulkActivation(false);
    });
  };

  public bulkActivation = (active: boolean): void => {
    if (this.gridApi) {
      const selectedNodes: IRowNode[] = this.gridApi.getSelectedNodes();
      const data: number[] = selectedNodes.map((row: IRowNode): number => { return (row.data as AlarmDO).id; });

      this.loadingStart();
      alarm<MsgRS>('status', undefined, data, { params: { active } })
        ?.then((resp: MsgRS) => {
          messageInfo('Success', resp.message, 5000);
          this.load().catch(() => { /* pass */ });
        })
        .catch((error: any) => {
          messageError('Error', getErrorMessage(error));
        })
        .finally(() => {
          this.loadingFinish();
        });
    }
  };

  public onGridReady = (event: GridReadyEvent): void => {
    // map the api
    this.gridApi = event.api;
  };

  public render(): JSX.Element {
    const { marked, showGrid, loading, data } = this.state;

    const columnsToHide: string[] = [
      'mailSubject',
      'mailBody',
      'typeSensorVariableId',
      'typeSensorVariableFormula',
      'gatewayId',
      'smsBody',
      'voiceBody',
      'sensorId',
      'repeat',
      'msgUnique',
      'msgUniqueSent',
      'companyId',
      'actionType',
      'timezone',
    ];
    const columnAlias: Record<string, string> = {
      selectedRow: ' ',
      id: 'ID',
      active: 'Active',
      sensorCode: 'Sensor Code',
      sensorName: 'Sensor Name',
      companyName: 'Company',
      action: 'Action',
      mailTo: 'Email',
      launch: 'Launched',
      disconnection: 'Disconnected',
      min: 'Min',
      max: 'Max',
      smsTo: 'Phone SMS',
      voiceTo: 'Phone Voice',
      minutes: 'Outside Bounds',
      accumulate: 'Cumulative',
      dateUpdate: 'Last Update',
    };
    const columnStyles: Record<string, any> = {
      id: { textAlign: 'right' },
      mailTo: { textAlign: 'left' }, // to
      launch: { textAlign: 'right' }, // ta
      disconnection: { textAlign: 'right' }, // nc
      min: { textAlign: 'right' },
      max: { textAlign: 'right' },
      smsTo: { textAlign: 'center' },
      voiceTo: { textAlign: 'center' },
      minutes: { textAlign: 'right' },
      accumulate: { textAlign: 'center' }, // tac_alarm
      active: { textAlign: 'center' },
      sensorCode: { textAlign: 'right' },
      sensorName: { textAlign: 'left' },
      action: { textAlign: 'left' },
      companyName: { textAlign: 'left' },
      dateUpdate: { textAlign: 'center' },
      actionButtons: { textAlign: 'center' },
    };
    const customColumnsDefs: Record<string, any> = {
      id: {
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        checkboxSelection: true,
        minWidth: 120,
      },
    };

    return (
      <div id="content" className="content">
        <div className="row">
          <div className="col-6 wdna-padding">
            <span>
              <b>{`Total Rows Selected: ${marked}`}</b>
            </span>
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            <button type="button" className="btn btn-success" onClick={this.activateSelected}>
              <b>Activate selected</b>
            </button>
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            <button type="button" className="btn btn-danger" onClick={this.deactivateSelected}>
              <b>Deactivate selected</b>
            </button>
          </div>
        </div>
        <div style={{ visibility: (showGrid ? 'visible' : 'hidden') }}>
          <Table
            data={data || []}
            alias={columnAlias}
            hiddenCols={columnsToHide}
            loading={loading}
            buttons={this.buttons}
            rowDataUpdated={this.rowDataUpdated}
            customCellStyles={columnStyles}
            customColumnsDefs={customColumnsDefs}
            onEdit={this.onEdit}
            onDelete={this.onDelete}
            onAdd={this.onEdit}
            onCopy={this.onCopy}
            onGridReady={this.onGridReady}
            onSelectionChanged={this.countSelected}
            selection="multiple"
            pagination={false}
            showRowNumber
          />
        </div>
      </div>
    );
  }
}
